import { AssetType, CardSize, CmsProperty } from '../../../api/models/Shared.types';
import { Log } from '@lightningjs/sdk';
import { DetailTranslationKeys, ErrorTranslationKeys, SettingsKeys } from '../../../api/models/TranslationKeys';
import { BundleApi } from '../../../api/BundleApi';
import { PriceApi } from '../../../api/price';
import { IActionButtonDefinition, IDetailModel, QuoteType } from '../detail.types';
import { BundleAsset } from './BundleAsset';
import { BundleDetailDescriptionDataSource } from './BundleDetailDescriptionDataSource';
import { DetailDataSource } from '../DetailDataSource';
import { RecommendationsUrlBuilder } from '../RecommendationsUrlBuilder';
import { RibbonDataSource } from '../../cms/RibbonDataSource';
import { VodApi } from '../../../api/VodApi';
import { VodAsset } from '../vod/VodAsset';
import { TabbedEpisodeRibbonDataSource } from '../../cms/TabbedEpisodeRibbonDataSource';
import { RentAction } from '../actions/RentAction';
import { ActionButton } from '../../../pages/detail/actionRowButtons/ActionButton';
import { atvPromiseAll } from '../../utility/PromiseUtil';
import { StaticPageUrls } from '../../../api/analytics/ClientEvents';
import { Backend } from '../../../api/Backend';
import { IBundlePriceResponse3, IValidQuotePriceResponse3 } from '../../../api/models/AndromeTV.types';
import { DataSource } from '../../cms/DataSource';

export class BundleDetailDataSource extends DetailDataSource {
  constructor(backend: Backend) {
    super(backend);
  }

  override logPageView() {
    this.backend.logService.trackPageView(StaticPageUrls.bundleDetail);
  }

  override load(id: string): Promise<IDetailModel> {
    return BundleApi.bundle(this.backend, id, this.backend.deviceProfile?.profile.catalog)
      .then(result => {
        const quotesProm = this._getQuotes(this.backend, id);
        const pricesProm = this._getPrices(this.backend, id);

        return atvPromiseAll([quotesProm, pricesProm]).then(([quotes, price]) => {
          const bundleAsset = new BundleAsset(result, quotes, price);

          return this.getRibbons(this.backend, bundleAsset).then(ribbons => {
            const descrLogic = new BundleDetailDescriptionDataSource(bundleAsset, AssetType.BUNDLE, this.backend);
            const images = {
              detailPoster: bundleAsset.bundle.image,
            };
            const res: IDetailModel = {
              description: descrLogic,
              images: images,
              buttons: this.getButtons(this.backend, bundleAsset),
              ribbonDatasources: ribbons,
            };
            return res;
          });
        });
      })
      .catch(err => {
        Log.error('[BundleDetailInitializer] error', err);
        throw this.backend.translations.forKey(ErrorTranslationKeys.error_general);
      });
  }

  getButtons(backend: Backend, bundle: BundleAsset): IActionButtonDefinition[] {
    const result = [];

    if (bundle.streamsLeftToRent() && !bundle.bundleIsExpired()) {
      const actionHandler = new RentAction(backend, AssetType.BUNDLE, bundle);
      const res: IActionButtonDefinition = {
        type: ActionButton,
        actionHandler,
        label: backend.translations.forKey(DetailTranslationKeys.detail_btn_rent_now),
      };
      result.push(res);
    }
    return result;
  }

  _getQuotes(backend: Backend, bundleId: string): Promise<IValidQuotePriceResponse3[]> {
    return backend.cachedQuoteApi
      .list(backend, backend.deviceProfile?.profile.catalog)
      .then(quotes => {
        return quotes.filter(q => q.entryId === bundleId && q.quoteType === QuoteType.BUNDLE);
      })
      .catch(err => {
        Log.error('[BundleDetailInitializer]', 'getQuotes error', err);
        return [];
      });
  }

  /**
   * @private
   */
  _getPrices(backend: Backend, id: string): Promise<IBundlePriceResponse3 | undefined> {
    if (backend.sessionInfo) {
      return PriceApi.forBundle(backend, id, backend.sessionInfo.device.entitlementId).catch(err => {
        Log.error('[BundleDetailInitializer]', 'getPrices error', err);
        return undefined;
      });
    }
    return new Promise<undefined>(() => {
      return undefined;
    });
  }

  getRibbons(backend: Backend, bundleAsset: BundleAsset): Promise<DataSource[]> {
    return new Promise(resolve => {
      if (bundleAsset.bundle.assets?.length === 1 && bundleAsset.bundle.assets[0]?.subType === AssetType.SERIES) {
        this.getSeasonRibbon(backend, bundleAsset.bundle.assets[0].id).then(result => {
          resolve([result]);
        });
      } else if (bundleAsset.bundle.assets && bundleAsset.bundle.assets.length > 0) {
        let bundleProperties = backend.globalSettings.stringArray(SettingsKeys.propertiesBundleRibbon);
        bundleProperties = bundleProperties ? bundleProperties : [CmsProperty.ShowProgress, CmsProperty.HideInfo];
        const bundleContentRibbon = {
          title: backend.translations.forKey(DetailTranslationKeys.detail_tab_included_in_bundle),
          properties: bundleProperties,
          url: RecommendationsUrlBuilder.bundleContentUrl(backend, bundleAsset.bundle.id),
          message: 'No bundle content',
          cardSize: CardSize.Normal,
        };

        resolve([new RibbonDataSource(backend, bundleContentRibbon, [], [])]);
      }
    });
  }

  getSeasonRibbon(backend: Backend, seriesVodId: string): Promise<TabbedEpisodeRibbonDataSource> {
    return VodApi.asset(backend, seriesVodId).then(result => {
      const vod = new VodAsset(result);
      return new TabbedEpisodeRibbonDataSource(
        backend,
        vod,
        backend.translations.forKey(DetailTranslationKeys.detail_tab_included_in_bundle)
      );
    });
  }
}
